import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft } from 'lucide-react';
import type { SaijikiEntry } from '../../../../lib/api';
import {
  DetailContainer,
  CloseButtonArea,
  CloseButton,
  ContentArea,
  SeasonEmoji,
  DetailTitle,
  DescriptionText,
  Divider,
  DetailText,
  ActionButtonArea,
  ActionButton,
} from './SaijikiDetailModal.styled';

const SEASON_EMOJI: Record<string, string> = {
  spring: '\uD83C\uDF38',
  summer: '\uD83C\uDF3B',
  autumn: '\uD83C\uDF41',
  winter: '\u2744\uFE0F',
};

const getSeasonEmoji = (season: string): string => SEASON_EMOJI[season] || '\uD83C\uDF3F';

interface SaijikiDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  saijikiData: SaijikiEntry;
  onStartConversation?: () => void;
}

export const SaijikiDetailModal: React.FC<SaijikiDetailModalProps> = ({
  isOpen,
  onClose,
  saijikiData,
  onStartConversation,
}) => {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return createPortal(
    <DetailContainer role="dialog" aria-modal="true" aria-labelledby="saijiki-detail-title">
      <CloseButtonArea>
        <CloseButton onClick={onClose} aria-label="閉じる">
          <ArrowLeft size={20} />
          閉じる
        </CloseButton>
      </CloseButtonArea>

      <ContentArea>
        <SeasonEmoji>{getSeasonEmoji(saijikiData.season)}</SeasonEmoji>
        <DetailTitle id="saijiki-detail-title">今日は「{saijikiData.title}」</DetailTitle>
        <DescriptionText>{saijikiData.description}</DescriptionText>

        {saijikiData.detail && (
          <>
            <Divider>── 昔の暮らしの知恵 ──</Divider>
            <DetailText>{saijikiData.detail}</DetailText>
          </>
        )}
      </ContentArea>

      {onStartConversation && (
        <ActionButtonArea>
          <ActionButton onClick={onStartConversation}>
            この話題でおはなしする
          </ActionButton>
        </ActionButtonArea>
      )}
    </DetailContainer>,
    document.body
  );
};

export default SaijikiDetailModal;
